import { IPhoneNumber } from "../types/interfaces";

export const identityCardTypes = ["Passport", "National ID", "Driver’s License"];
export const dateFormat = 'YYYY-MM-DD';
export const getPhoneNumbersArr = (initPhoneNumbers: any): string[] => {
    const phonesArr: string[] | null = [];
    initPhoneNumbers?.forEach((item: IPhoneNumber) => {
        if (item.phoneNumber != null) {
            phonesArr.push(item.phoneNumber);
        }
    });
    return phonesArr;
}

export const KYC_STATUS = {
  PENDING: 1,
  CONFIRMED: 2,
  REJECTED: 3,
  PRE_APPROVE: 4
} as const;

export const KYC_STATUS_LEGACY = {
  NEW: 'New',
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
  PREAPPROVED: 'Preapproved'
} as const;

export const KYC_STATUS_DISPLAY = {
  [KYC_STATUS.PENDING]: 'Under Review',
  [KYC_STATUS.CONFIRMED]: 'Verified',
  [KYC_STATUS.REJECTED]: 'Needs Update',
  [KYC_STATUS.PRE_APPROVE]: 'Verified'
} as const;

export const KYC_STATUS_TO_LEGACY = {
  [KYC_STATUS.PENDING]: KYC_STATUS_LEGACY.PENDING,
  [KYC_STATUS.CONFIRMED]: KYC_STATUS_LEGACY.CONFIRMED,
  [KYC_STATUS.REJECTED]: KYC_STATUS_LEGACY.REJECTED,
  [KYC_STATUS.PRE_APPROVE]: KYC_STATUS_LEGACY.PREAPPROVED
} as const;

export const LEGACY_TO_KYC_STATUS = {
  [KYC_STATUS_LEGACY.NEW]: KYC_STATUS.PENDING,
  [KYC_STATUS_LEGACY.PENDING]: KYC_STATUS.PENDING,
  [KYC_STATUS_LEGACY.CONFIRMED]: KYC_STATUS.CONFIRMED,
  [KYC_STATUS_LEGACY.REJECTED]: KYC_STATUS.REJECTED,
  [KYC_STATUS_LEGACY.PREAPPROVED]: KYC_STATUS.PRE_APPROVE
} as const;

export type KycStatusNumeric = typeof KYC_STATUS[keyof typeof KYC_STATUS];
export type KycStatusLegacy = typeof KYC_STATUS_LEGACY[keyof typeof KYC_STATUS_LEGACY];
export type KycStatusValue = KycStatusNumeric | KycStatusLegacy;

export const normalizeKycStatus = (status: KycStatusValue | null | undefined): KycStatusNumeric | null => {
  if (status === null || status === undefined) {
    return null;
  }

  if (typeof status === 'number') {
    return status as KycStatusNumeric;
  }

  return LEGACY_TO_KYC_STATUS[status as KycStatusLegacy] || null;
};

export const isKycStatusValid = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  if (normalizedStatus === null) {
    return false;
  }

  return normalizedStatus === KYC_STATUS.CONFIRMED || normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const isKycStatusConfirmedOrPreapproved = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.CONFIRMED || normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const isKycStatusPreapproved = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const isKycStatusRejected = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.REJECTED;
};

export const isKycStatusPending = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.PENDING;
};

export const isKycStatusConfirmed = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.CONFIRMED;
};

export const getKycStatusDisplayName = (status: KycStatusValue | null | undefined): string => {
  const normalizedStatus = normalizeKycStatus(status);
  if (normalizedStatus === null) {
    return 'Unknown';
  }
  return KYC_STATUS_DISPLAY[normalizedStatus] || 'Unknown';
};

export const getKycStatusLegacyValue = (status: KycStatusValue | null | undefined): string | null => {
  const normalizedStatus = normalizeKycStatus(status);
  if (normalizedStatus === null) {
    return null;
  }
  return KYC_STATUS_TO_LEGACY[normalizedStatus] || null;
};

export const doesKycStatusRequireAction = (status: KycStatusValue | null | undefined): boolean => {
  const normalizedStatus = normalizeKycStatus(status);
  return normalizedStatus === KYC_STATUS.REJECTED || normalizedStatus === KYC_STATUS.PRE_APPROVE;
};

export const OCCUPATION_OPTIONS = [
  { label: 'Politicians', value: 'Politicians', id: 1 },
  { label: 'Civil Servants', value: 'Civil_servants', id: 2 },
  { label: 'Gas Station Owners', value: 'Gas_station_owners', id: 3 },
  { label: 'Retail Business Owners', value: 'Retail_business_owners', id: 4 },
  { label: 'Car Dealerships', value: 'Car_Dealerships', id: 5 },
  { label: 'Travel Agencies', value: 'Travel_Agencies', id: 6 },
  { label: 'Investment Brokers', value: 'Investment_Brokers', id: 7 },
  { label: 'Jewelry Dealers', value: 'Jewelry_dealers', id: 8 },
  { label: 'Lawyers', value: 'Lawyers', id: 9 },
  { label: 'Accountants', value: 'Accountants', id: 10 },
  { label: 'Other', value: 'Other', id: 11 },
  { label: 'Financial Services', value: 'Financial_Services', id: 12 },
  { label: 'Hospitality', value: 'Hospitality', id: 13 },
  { label: 'Banker', value: 'Banker', id: 14 },
  { label: 'Information Technology Financial Services', value: 'Information_Technology_Financial_Services', id: 15 },
  { label: 'Entrepreneur', value: 'Entrepreneur', id: 16 },
  { label: 'Plumber', value: 'Plumber', id: 17 },
  { label: 'Graphic Designer', value: 'Graphic_designer', id: 18 },
  { label: 'Photographer', value: 'Photographer', id: 19 },
  { label: 'Journalist', value: 'Journalist', id: 20 },
  { label: 'Brand Manager', value: 'Brand_manager', id: 21 },
  { label: 'Event Planner', value: 'Event_planner', id: 22 },
  { label: 'Auto Mechanic', value: 'Auto_mechanic', id: 23 },
  { label: 'Landscaper', value: 'Landscaper', id: 24 },
  { label: 'Groundskeeper', value: 'Groundskeeper', id: 25 },
  { label: 'Fisherman', value: 'Fisherman', id: 26 }
];

export const OCCUPATION_BACKEND_TO_LABEL = {
  'Politicians': 'Politicians',
  'Civil_servants': 'Civil Servants',
  'Gas_station_owners': 'Gas Station Owners',
  'Retail_business_owners': 'Retail Business Owners',
  'Car_Dealerships': 'Car Dealerships',
  'Travel_Agencies': 'Travel Agencies',
  'Investment_Brokers': 'Investment Brokers',
  'Jewelry_dealers': 'Jewelry Dealers',
  'Lawyers': 'Lawyers',
  'Accountants': 'Accountants',
  'Other': 'Other',
  'Financial_Services': 'Financial Services',
  'Hospitality': 'Hospitality',
  'Banker': 'Banker',
  'Information_Technology_Financial_Services': 'Information Technology Financial Services',
  'Entrepreneur': 'Entrepreneur',
  'Plumber': 'Plumber',
  'Graphic_designer': 'Graphic Designer',
  'Photographer': 'Photographer',
  'Journalist': 'Journalist',
  'Brand_manager': 'Brand Manager',
  'Event_planner': 'Event Planner',
  'Auto_mechanic': 'Auto Mechanic',
  'Landscaper': 'Landscaper',
  'Groundskeeper': 'Groundskeeper',
  'Fisherman': 'Fisherman'
};

export const OCCUPATION_OPTIONS_FOR_DROPDOWN = [
  'Politicians',
  'Civil Servants',
  'Gas Station Owners',
  'Retail Business Owners',
  'Car Dealerships',
  'Travel Agencies',
  'Investment Brokers',
  'Jewelry Dealers',
  'Lawyers',
  'Accountants',
  'Other',
  'Financial Services',
  'Hospitality',
  'Banker',
  'Information Technology Financial Services',
  'Entrepreneur',
  'Plumber',
  'Graphic Designer',
  'Photographer',
  'Journalist',
  'Brand Manager',
  'Event Planner',
  'Auto Mechanic',
  'Landscaper',
  'Groundskeeper',
  'Fisherman'
];

export const getOccupationLabel = (value: string | number): string => {
  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseInt(value)))) {
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    const occupationOption = OCCUPATION_OPTIONS.find(option => option.id === numValue);
    return occupationOption ? occupationOption.label : (value?.toString() || 'N/A');
  }
  
  if (typeof value === 'string') {
    return (OCCUPATION_BACKEND_TO_LABEL as any)[value] || value;
  }
  
  return String(value || 'N/A');
};